import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { saveAs } from 'file-saver';

import { useResizeObserver } from 'usehooks-ts';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { Table, Button, Input, DatePicker, Modal, Tooltip, Form, Switch, Flex, Select, notification, Tag, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';

import {
    LoadingOutlined,
    ReloadOutlined,
    CalendarOutlined,
    EditOutlined,
    CheckCircleFilled,
    UnorderedListOutlined,
    QuestionCircleOutlined,
    FileExcelOutlined,
    CheckOutlined,
    WarningOutlined,
    FormOutlined,
    CheckCircleOutlined,
    EyeFilled,
} from '@ant-design/icons';

import BillModal, { IBillParams } from '@src/components/logistics/import/bills/bill-modal';

import BillNoTruckModal from '@components/balance-details/bill-modal';
import ConsignmentCard from '@components/consignment-card/consignment-card';

import TariffCard from '@src/controls/tariff-card/tariff-card';

import FormHeader from '@controls/form-header/form-header';
import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';
import Bill from '@controls/bill/bill';
import Comment from '@controls/comment/comment';
import Tags from '@controls/tags/tags';

import { serverFetch } from '@src/server';

import { securityRestriction } from '@src/extensions/notification';
import { exception } from '@extensions/notification';
import { toFinanceString, dataURItoBlob, getEnumList, getDate } from '@extensions/utils';
import { userLoaded, setFilter } from '@store/actions';

import { IUserSession } from '@src/entities/user-session';
import { IBillFilter } from '@src/entities/bill-filter';

import { IBill } from '@entities/bill';
import { IUser } from '@entities/user';
import { ICity } from '@entities/city';
import { ITruck } from '@entities/truck';
import { IItem } from '@entities/item';
import { ICountry } from '@entities/country';
import { ICountryItem } from '@entities/country-item';

import { Permission, hasPermission } from '@src/enums/permission';
import { UnitType } from '@enums/unit-type';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { BillStatus, enumLabel as billStatusLabel } from '@enums/bill-status';
import { IEnumItem } from '@enums/enum-item';

import { AirplaneIcon, PenIcon } from '@icons/index';

dayjs.extend(utc);

interface IBillHeader {
    billDate?: Dayjs;
    totalPrice: number;
    totalPriceFinal: number;
    totalPriceRub: number;
    totalPriceFinalRub: number;
    bills: Array<IBill>;
}

interface ILoadingOnParams {
    billIds: Array<string>;
    loadingOn: Dayjs;
}

const filterContext: string = 'BillHeaders';

const BillHeaders = () => {
    const { userId } = useParams();

    const initFilter: IBillFilter = {
        userId: userId,
        statuses: [BillStatus.ToVerify, BillStatus.Completed],
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [editBillForm] = Form.useForm();
    const [loadingOnForm] = Form.useForm();

    const { RangePicker } = DatePicker;

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IBillFilter>((s) => s.filters[filterContext]);

    const [modal, modalContextHolder] = Modal.useModal();
    const [api, notificationContextHolder] = notification.useNotification();

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [headers, setHeaders] = useState<Array<IBillHeader>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [openBill, setOpenBill] = useState<boolean>(false);
    const [bills, setBills] = useState<Array<IBill>>([]);
    const [currentBill, setCurrentBill] = useState<IBill>();
    const [user, setUser] = useState<IUser>();
    const [editBillOpen, setEditBillOpen] = useState<boolean>(false);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [trucks, setTrucks] = useState<Array<ITruck>>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [items, setItems] = useState<Array<IItem>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);

    const [openLoadingOnModal, setOpenLoadingOnModal] = useState<boolean>(false);

    const [editBillNoTruckOpen, setEditBillNoTruckOpen] = useState<boolean>(false);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(BillStatus, billStatusLabel));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            editBillForm.resetFields();

            const promises = [
                await serverFetch(`users/info/${userId}`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('bills/trucks', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('countries', { method: 'GET', queryParams: { withTransportOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('countries/items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUser(result[0][0] || []);
                setCities(result[0][1] || []);
                setTrucks(
                    (result[0][2] || []).sort((a: ITruck, b: ITruck) => (dayjs(a.departureOn).isAfter(dayjs(b.departureOn)) ? -1 : 1)),
                );
                setCountries(result[0][3].filter((c: ICountry) => c.generateTariff));
                setItems(result[0][4] || []);
                setCountryItems(result[0][5]);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setSelectedIds([]);
        setHeaders([]);

        const fetchData = async () => {
            editBillForm.resetFields();

            setLoading(true);

            const promises = [
                await serverFetch('bills/headers', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения счетов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setHeaders(result[0][0] || []);
                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter || filter.userId != userId) {
            d(setFilter(initFilter, filterContext));
            return;
        }
    }, [filter]);

    useEffect(() => {
        if (bills && bills.length > 0) {
            setOpenBill(true);
        }
    }, [bills]);

    const onSelectChange = (selectedRowKeys: React.Key[], selectedRows: Array<IBill>) => {
        if (selectedRows.length == 1) {
            const bill: IBill = selectedRows[0];

            if (bill.status != BillStatus.Abandoned) {
                setCurrentBill(bill);
            }
        } else {
            setCurrentBill(undefined);
        }

        const selectedBills = selectedRows.filter((r) => r.status != BillStatus.Abandoned);
        const ids = selectedBills.map((b) => b.id);
        setSelectedIds(ids);
    };

    const onTariffUpdate = (entity: IBillParams) => {
        entity.billIds = selectedIds as Array<string>;

        serverFetch(`bills/tariff`, { method: 'PUT', bodyData: entity })
            .then(() => {
                setEditBillOpen(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка изменения тарифа', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetloadingOn = (entity: ILoadingOnParams) => {
        entity.billIds = selectedIds as string[];
        entity.loadingOn = dayjs(entity.loadingOn).utc(true).set('hour', 0).set('minute', 0).set('second', 0);

        serverFetch(`bills/loadingOn`, { method: 'POST', bodyData: entity })
            .then(() => {
                loadingOnForm.resetFields();

                setOpenLoadingOnModal(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка изменения счета', ex, () => d(userLoaded(undefined)));
            });
    };

    const onUpload = async (id: string, file: Blob): Promise<string> => {
        const formData = new FormData();
        formData.append('file', file);

        return serverFetch(`bills/${id}/invoice/upload`, { method: 'POST', body: formData })
            .then((data) => {
                return data;
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка загрузки файла', ex, () => d(userLoaded(undefined)));
                return undefined;
            });
    };

    const onExport = () => {
        serverFetch('bills/export', { method: 'GET', queryParams: filter })
            .then((data) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex) => {
                exception(api, 'Ошибка выгрузки счетов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSaveBill = (entity: IBill) => {
        if (!entity) return;

        serverFetch(`bills`, { method: entity.id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setEditBillNoTruckOpen(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка выставления счета', ex, () => d(userLoaded(undefined)));
            });
    };

    const onComplete = () => {
        setLoading(true);

        serverFetch(`bills/complete`, { method: 'POST', bodyData: selectedIds })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка проведения тарифов', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 130 }}
                            key='number'
                            placeholder='Номер счета'
                            value={filter?.number}
                            onChange={(data) => {
                                d(setFilter({ ...filter, number: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 250 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 230 }}
                            key='legalName'
                            placeholder='Контрагент'
                            value={filter?.legalName}
                            onChange={(data) => {
                                d(setFilter({ ...filter, legalName: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='country'
                            placeholder='Страна'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.countryIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, countryIds: value }, filterContext))}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={countries.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Select
                            key='item'
                            placeholder='Продукт'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.itemIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, itemIds: value }, filterContext))}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={items.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            style={{ width: 240 }}
                            key='truck'
                            placeholder='Машина'
                            value={filter?.carrierIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            onChange={(value: any) => d(setFilter({ ...filter, truckIds: value }, filterContext))}
                            showSearch={{
                                filterOption: (input: any, option: any) =>
                                    (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                            }}
                            options={trucks.map((t) => {
                                return { value: t.id, label: `${getDate(t.departureOn)} / ${t.number}` };
                            })}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((s) => {
                                return { value: s.id, label: s.name };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='loading'
                            format='DD.MM.YYYY'
                            placeholder={['Дата с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[filter?.dateFrom ? dayjs(filter?.dateFrom) : null, filter?.dateTo ? dayjs(filter?.dateTo) : null]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                dateFrom: undefined,
                                                dateTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            dateFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            dateTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Form.Item key='isChanged' label='Измененные' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isChanged}
                                onChange={(value: boolean) => {
                                    d(setFilter({ ...filter, isChanged: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentBill,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (currentBill?.boxGroupId) {
                                if (
                                    !hasPermission(userSession.permissions, Permission.ManageTransportBill) &&
                                    !hasPermission(userSession.permissions, Permission.EditTransportBill)
                                ) {
                                    securityRestriction(api, [Permission.ManageTransportBill, Permission.EditTransportBill]);
                                    return;
                                }

                                editBillForm.setFieldsValue({
                                    id: currentBill?.id,
                                    boxGroupId: currentBill?.boxGroupId,
                                    cityId: currentBill?.cityId,
                                    volumeWeight: currentBill?.volumeWeight,
                                    grossWeight: currentBill?.grossWeight,
                                    handlingPrice: currentBill?.handlingPrice,
                                    precoolingPrice: currentBill?.precoolingPrice,
                                    totalPrice: currentBill?.totalPrice,
                                });

                                setEditBillOpen(true);
                            } else {
                                setEditBillNoTruckOpen(true);
                            }
                        },
                    },
                    {
                        label: 'Изменить дату отгрузки',
                        key: 'setLoadingOn',
                        disabled: loading || selectedIds.length <= 0,
                        icon: <CalendarOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTransportBill)) {
                                securityRestriction(api, [Permission.ManageTransportBill]);
                                return;
                            }

                            setOpenLoadingOnModal(true);
                        },
                    },
                    {
                        label: 'Провести',
                        key: 'completed',
                        disabled: loading || selectedIds.length <= 0,
                        icon: <CheckOutlined />,
                        type: 'primary',
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTransportBill)) {
                                securityRestriction(api, [Permission.ManageTransportBill]);
                                return;
                            }

                            modal.confirm({
                                title: `Провести выбранные тарифы?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onComplete();
                                },
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Выгрузить в Excel',
                        key: 'exportToExcel',
                        icon: <FileExcelOutlined />,
                        onClick: () => onExport(),
                    },
                    /* 
                    {
                        label: 'Test Print',
                        key: 'test print',
                        onClick: () => onPrint(),
                    }, */
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const expandedBills = (record: IBillHeader) => {
        const columns: ColumnsType<IBill> = [
            {
                title: '#',
                align: 'center',
                width: 100,
                onCell: (record) => ({
                    style: {
                        verticalAlign: 'top',
                    },
                }),
                render: (_, record) => {
                    let comment: string = '';
                    if (record.consignmentComment) {
                        comment = 'Поставка:' + '\n' + record.consignmentComment + '\n\n';
                    }

                    if (record.comment) {
                        comment += 'Счет:' + '\n' + record.comment;
                    }
                    return (
                        <Flex vertical gap='small' align='center' justify='center'>
                            <Button
                                variant='link'
                                color='blue'
                                style={{ color: '#1677ff', fontWeight: 600 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setBills([record]);
                                }}
                            >
                                {record.fullNumber}
                            </Button>

                            <Flex justify='center' align='center'>
                                {record.boxGroupId && (
                                    <Tooltip title='Детали заказа'>
                                        <Button
                                            size='large'
                                            variant='link'
                                            color='blue'
                                            target='_blank'
                                            href={`/orders/${record?.userId}/${record?.boxGroupId}`}
                                            icon={<UnorderedListOutlined />}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Tooltip>
                                )}

                                {comment && <Comment comment={comment} />}
                            </Flex>

                            <Flex gap='small' align='center' justify='center'>
                                <Tooltip
                                    placement='top'
                                    title={
                                        record.status == BillStatus.Completed ||
                                        record.status === BillStatus.Paid ||
                                        record.status == BillStatus.Abandoned
                                            ? 'Расчет Проведен'
                                            : 'Счет ожидает проверки'
                                    }
                                    color={
                                        record.status == BillStatus.Completed ||
                                        record.status === BillStatus.Paid ||
                                        record.status == BillStatus.Abandoned
                                            ? 'var(--main-green)'
                                            : 'darkorange'
                                    }
                                >
                                    <>
                                        {(record.status == BillStatus.Completed ||
                                            record.status === BillStatus.Paid ||
                                            record.status == BillStatus.Abandoned) && (
                                            <CheckCircleOutlined
                                                style={{
                                                    color: 'var(--main-green)',
                                                    fontSize: 18,
                                                }}
                                            />
                                        )}

                                        {record.status == BillStatus.ToVerify && (
                                            <Tooltip placement='top' title='Счет ожидает проверки' color='#f6546a'>
                                                <WarningOutlined
                                                    style={{
                                                        color: '#f6546a',
                                                        fontSize: 18,
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </>
                                </Tooltip>

                                {record.isChanged && (
                                    <Tooltip title={`Счет изменен ${getDate(record.changedOn)}`}>
                                        <div>
                                            <PenIcon style={{ color: 'var(--main-green)' }} />
                                        </div>
                                    </Tooltip>
                                )}
                            </Flex>

                            {record.tag && <Tags label={record.tag} />}
                        </Flex>
                    );
                },
            },
            {
                title: '',
                width: 400,
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 5,
                    style: {
                        verticalAlign: record.boxGroupId ? 'top' : 'middle',
                    },
                }),
                render: (_: any, record) => {
                    if (!record.boxGroupId) {
                        return record.comment;
                    }

                    return (
                        <Form colon={false} labelCol={{ xs: 9 }} wrapperCol={{ xs: 15 }}>
                            <Form.Item label='ID' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.consigneeCode}</span>
                            </Form.Item>
                            <Form.Item label='Маркировка' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.markingCode}</span>
                            </Form.Item>
                            <Form.Item label='Контрагент' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.legalName}</span>
                            </Form.Item>
                        </Form>
                    );
                },
            },
            {
                align: 'center',
                width: 375,
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                    style: {
                        verticalAlign: 'top',
                    },
                }),
                render: (_, record) => {
                    let diffQty: number = 0;

                    const country = countries.find((c) => c.id == record.countryId);
                    if (!country) return <div></div>;

                    return (
                        <ConsignmentCard
                            country={country}
                            api={api}
                            items={[
                                {
                                    itemName: record.itemName,
                                    itemColor: record.itemColor,
                                    qty: record.qty,
                                    diffQty: diffQty,
                                    loadingOn: record.billDate,
                                    awbNumber: record.awbNumber,
                                    cargoCarrierName: record.cargoCarrierName,
                                    truckNumber: record.truckNumber,
                                    comment: record.boxGroupComment,
                                },
                            ]}
                            serverFetch={serverFetch}
                        />
                    );
                },
            },
            {
                title: 'Вес',
                width: 180,
                align: 'center',
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                    style: {
                        verticalAlign: 'top',
                    },
                }),
                render: (_: any, record) => {
                    if (record.unitType !== UnitType.Weight) return '';

                    return (
                        <Form colon={false} labelCol={{ xs: 12 }} wrapperCol={{ xs: 12 }}>
                            <Form.Item label='Брутто' style={{ marginBottom: 0 }}>
                                {record.grossWeight && (
                                    <Flex justify='start' style={{ fontWeight: 600 }}>
                                        {record.grossWeight} <span>кг</span>
                                    </Flex>
                                )}
                            </Form.Item>
                            <Form.Item label='Объемный'>
                                {record.volumeWeight && (
                                    <Flex justify='start' style={{ fontWeight: 600 }}>
                                        {record.volumeWeight} <span>кг</span>
                                        <Tooltip title='Расчетный вес'>
                                            <CheckCircleFilled style={{ color: '#FFDD2D', marginLeft: 5 }} />
                                        </Tooltip>
                                    </Flex>
                                )}
                            </Form.Item>
                        </Form>
                    );
                },
            },
            {
                title: 'Тариф',
                width: 100,
                align: 'center',
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                    style: {
                        verticalAlign: 'top',
                    },
                }),
                render: (_: any, record) => {
                    if (!record.boxGroupId) return;

                    return (
                        <TariffCard
                            handlingPrice={record.handlingPrice}
                            handlingPriceRequired={true}
                            precoolingPrice={record.precoolingPrice}
                            precoolingPriceRequired={true}
                        />
                    );
                },
            },
            {
                title: `Итого (${currencySign(Currency.Usd)})`,
                width: 140,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                        verticalAlign: 'top',
                    },
                }),
                render: (_: any, record) => {
                    return (
                        <Flex vertical gap='small' justify='center' style={{ color: record.totalPrice <= 0 ? 'red' : '' }}>
                            <Flex gap='small' justify='center' style={{ color: record.totalPrice <= 0 ? 'red' : '' }}>
                                {record.totalPrice && toFinanceString(record.totalPrice, 2)}
                                {record.isTotalPriceManual && (
                                    <Tooltip title='Счет изменен вручную'>
                                        <FormOutlined />
                                    </Tooltip>
                                )}
                            </Flex>

                            <div style={{ padding: 3, marginTop: 5 }}>
                                {record.status == BillStatus.Paid && (
                                    <Tag style={{ marginRight: 0 }} color='var(--main-green)' variant='solid'>
                                        ОПЛАЧЕН
                                    </Tag>
                                )}
                            </div>
                        </Flex>
                    );
                },
            },
            {
                title: `Итого (${currencySign(Currency.Rub)})`,
                width: 140,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                        verticalAlign: 'top',
                    },
                }),
                render: (_: any, record) => {
                    return (
                        <div style={{ color: record.totalPriceRub <= 0 ? 'red' : '' }}>
                            {record.totalPriceRub && toFinanceString(record.totalPriceRub, 2)}
                        </div>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={record.bills}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'checkbox',
                    columnWidth: 32,
                    getCheckboxProps: (record) => ({
                        style: { display: record.status == BillStatus.Abandoned ? 'none' : '' },
                    }),
                    onCell: (record) => ({
                        style: {
                            verticalAlign: 'top',
                        },
                    }),
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([], [record]);
                        },
                    };
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IBillHeader> = [
            {
                title: 'Отгрузка',
                width: 130,
                align: 'center',
                render: (_, record) => {
                    return record.billDate && getDate(record.billDate);
                },
            },
            {
                align: 'center',
                width: 40,
                render: (_, record) => {
                    return <Button type='text' icon={<EyeFilled />} onClick={() => setBills(record.bills)} />;
                },
            },
            {
                title: `Общий Счет (${currencySign(Currency.Usd)})`,
                align: 'left',
                width: 300,
                onCell: (record) => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex align='center' gap='large'>
                            {record.totalPrice && toFinanceString(record.totalPrice, 2)}
                            {record.totalPriceFinal > 0 && record.totalPriceFinal != record.totalPrice && (
                                <Tooltip title='Со скидкой'>
                                    <div style={{ background: '#d2dcd8', padding: '3px 6px' }}>
                                        {toFinanceString(record.totalPriceFinal, 2)}
                                    </div>
                                </Tooltip>
                            )}
                        </Flex>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='billDate'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                expandable={{
                    columnWidth: 32,
                    expandedRowRender: expandedBills,
                }}
                dataSource={headers}
                pagination={false}
                virtual={true}
                scroll={{ y: height + (showFilter ? 0 : 15), x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px - ${userId ? 68 : 0}px)` }}>
            {userId && <FormHeader title={`Счета "${user?.login}"`} />}

            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {openBill && (
                <Bill
                    bills={bills}
                    onClose={() => {
                        setBills([]);
                        setOpenBill(false);
                    }}
                />
            )}

            {currentBill && editBillOpen && (
                <BillModal
                    bill={currentBill}
                    cities={cities}
                    countries={countries}
                    items={countryItems.filter((i) => i.countryId == currentBill.countryId)}
                    onSave={onTariffUpdate}
                    onCancel={() => setEditBillOpen(false)}
                />
            )}

            <Modal
                width={300}
                title='Изменить дату отгрузки'
                destroyOnHidden={true}
                open={openLoadingOnModal}
                okText='ОК'
                closable={false}
                onOk={() => {
                    loadingOnForm.submit();
                }}
                onCancel={() => {
                    loadingOnForm.resetFields();
                    setCurrentBill(undefined);
                    setSelectedIds([]);

                    setOpenLoadingOnModal(false);
                }}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 9 }}
                    wrapperCol={{ span: 15 }}
                    style={{ marginTop: 20 }}
                    form={loadingOnForm}
                    onFinish={onSetloadingOn}
                >
                    <Form.Item label='Дата' name='loadingOn' rules={[{ required: true, message: 'Укажите дату отгрузки' }]}>
                        <DatePicker format={'DD.MM.YYYY'} />
                    </Form.Item>
                </Form>
            </Modal>

            {currentBill && editBillNoTruckOpen && (
                <BillNoTruckModal
                    userId={currentBill?.userId || ''}
                    userLogin={user?.login || ''}
                    billId={currentBill?.id}
                    currency={Currency.Usd}
                    api={api}
                    onCancel={() => setEditBillNoTruckOpen(false)}
                    onSave={(value: IBill) => onSaveBill(value)}
                    serverFetch={serverFetch}
                />
            )}

            {modalContextHolder}
            {notificationContextHolder}
        </div>
    );
};

export default BillHeaders;
